/**
 * publishModalHelpers — the non-component helpers shared by the publish
 * confirmation modal's parts: crew filtering, the schedule summary, and the
 * disabled-button style.
 *
 * Split out of PublishConfirmModal alongside its sub-components so each
 * component file declares exactly one component (`react/no-multi-comp`).
 * Behaviour is unchanged.
 */
import type { CSSProperties } from 'react';

import type { WorkshopCrewMember, WorkshopDetail } from '../../../../api/workshops';

import { formatClock, formatDate, formatDuration } from './workshopFields';

/** The crew members for one position, in insertion order. */
export function membersAt(staff: WorkshopCrewMember[], position: string): WorkshopCrewMember[] {
  return staff.filter((s) => s.position === position);
}

/**
 * What the When row can show, plus a warning when it can't show all of it.
 * Publishing is irreversible and this summary is what the operator confirms
 * against, so an unpopulated venue-local column must say so in words — it used
 * to render "— · — · —" and read as decoration.
 */
export function scheduleSummary(workshop: WorkshopDetail): {
  value: string;
  warning: string | null;
} {
  const { localStartDatetime: start, localEndDatetime: end } = workshop;
  if (!start) {
    return {
      value: 'Not available',
      warning: 'This workshop’s date and time aren’t available — check the schedule first.',
    };
  }
  const when = `${formatDate(start)} · ${formatClock(start)}`;
  if (!end) {
    return {
      value: when,
      warning: 'This workshop’s end time isn’t available — check the schedule first.',
    };
  }
  return { value: `${when} · ${formatDuration(start, end)}`, warning: null };
}

/** Visible note when the schedule can't be shown. Deliberately does NOT block
 *  Confirm — whether missing data should stop a publish is a product decision;
 *  making the gap legible is not. */

/** Visual affordance for a disabled button — `nb-btn` has no `:disabled` rule,
 *  so a disabled button otherwise still reads as clickable. */
export function disabledStyle(disabled: boolean): CSSProperties | undefined {
  return disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined;
}
